import { CoreApi, Dimension, Product, ProductType } from '@inter-ikea-storageone/range';
import { Entity, HierarchyUtils, Nullable } from '@inter-ikea-core/core';
import { useCore } from 'contexts/core';
import { SelectedProduct } from 'ducks/products';
import { FilterOption, getUnit } from 'filters/SizeFilter/utils';
import usePrevious from 'hooks/usePrevious';
import * as R from 'ramda';
import { useEffect, useState } from 'react';
import {
  getDoorsFromSelectedEntity,
  getMatchingDoorsWithHandles,
  getMatchingSlidingDoors
} from '../../utils/changeSize/doorUtils';
import { getProductFromEntity } from 'rangeConfig/shared/utils/getProductFromEntity';
import SizeToggle from 'ui/SizeToggle/SizeToggle';
import { getDimensionOptions } from '../../utils/utils';

const getHeightOfFrameAndLight = (
  coreApi: CoreApi,
  selectedEntity: Nullable<Entity>,
  availableHeights: any,
  products: Product[]
) => {
  const arrHeights = Object.keys(availableHeights);

  const arrHeightsConverted = arrHeights.map(i => Number(i));
  const highestFrame = Math.max(...arrHeightsConverted);

  const outerLights = products.filter(p => {
    return p.type === ProductType.PAX_OUTER_LIGHT;
  });

  const availableLights = getDimensionOptions('height')(outerLights);

  const arrLights = Object.keys(availableLights);
  const arrLightsConverted = arrLights.map(i => Number(i));
  const highestLight = Math.max(...arrLightsConverted);

  const hasOuterLights =
    selectedEntity &&
    HierarchyUtils.children(selectedEntity).filter(entity => {
      return coreApi.entities.isProductType(entity, ProductType.PAX_OUTER_LIGHT);
    });

  return { hasOuterLights, highestFrame, highestLight };
};

const Height = ({
  title,
  products,
  selectedProduct,
  selectedEntity,
  onSizeChange,
  framesWithSameDepth,
  availableHeights
}: {
  title?: string;
  fluidWidth?: boolean;
  products: Product[];
  selectedProduct: SelectedProduct | null;
  selectedEntity: Nullable<Entity>;
  onSizeChange: (
    size: number,
    setFilterState: any,
    framesMatchingSelectedFrame: any,
    replaceableDoors: any,
    matchFrameBy: Dimension,
    dimensionType: Dimension
  ) => void;
  framesWithSameDepth: Product[];
  availableHeights: FilterOption;
}) => {
  const [filterState, setFilterState] = useState({ selected: 0 });
  const coreApi = useCore();
  const previousDoors = getDoorsFromSelectedEntity(coreApi, selectedEntity, products);
  const [replaceableDoors, setReplaceableDoors] = useState<any>([]);
  const prevDoors = usePrevious(replaceableDoors);

  // Get the current markets unit
  const unit = getUnit(products);

  useEffect(() => {
    const changeSlidingDoors = (slidingDoorProduct: Product) => {
      const slidingDoorsMatchingCurrentDoors = getMatchingSlidingDoors(
        slidingDoorProduct,
        products,
        'height'
      );

      if (!R.equals(prevDoors, slidingDoorsMatchingCurrentDoors)) {
        setReplaceableDoors(slidingDoorsMatchingCurrentDoors);
      }
    };
    if (!selectedEntity) return;
    const slidingDoor = coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(
      selectedEntity,
      [ProductType.PAX_SLIDING_DOOR_REGULAR_ASM, ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM]
    );

    if (slidingDoor) {
      const slidingDoorProduct = getProductFromEntity(slidingDoor, products, coreApi);
      if (!slidingDoorProduct) return;

      changeSlidingDoors(slidingDoorProduct);
    } else {
      const doorsMatchingCurrentDoorsWithHandles = getMatchingDoorsWithHandles(
        previousDoors,
        products,
        coreApi,
        'height'
      ) as { doors: Product[]; handleId?: string }[];

      if (!R.equals(prevDoors, doorsMatchingCurrentDoorsWithHandles)) {
        setReplaceableDoors(doorsMatchingCurrentDoorsWithHandles);
      }
    }
  }, [
    coreApi,
    prevDoors,
    previousDoors,
    products,
    replaceableDoors,
    replaceableDoors.length,
    selectedEntity
  ]);

  // Set the checked height to current selected products height
  useEffect(() => {
    if (selectedProduct) {
      setFilterState({ selected: selectedProduct?.communicatedDimensions.height });
    }
  }, [selectedProduct, selectedProduct?.communicatedDimensions]);

  if (!selectedProduct) return null;

  // Get room and outerLights maxheight to calculate minimum roomheight to show/hide widget
  const roomSize = coreApi.kvadratRoom?.getDimensions();
  const { hasOuterLights, highestFrame, highestLight } = getHeightOfFrameAndLight(
    coreApi,
    selectedEntity,
    availableHeights,
    products
  );
  const roofMargin = 1;
  const lightHeight = hasOuterLights && hasOuterLights.length > 0 ? highestLight : 0;

  const sizes = Object.entries(availableHeights).map(([size, communicated]) => {
    return { value: parseInt(size, 10), text: `${communicated}`, unit: `${unit}` };
  });
  const currentSize = filterState.selected;
  const disabledSizes =
    roomSize && roomSize?.y < highestFrame + roofMargin + lightHeight
      ? [highestFrame.toString()]
      : [];

  return sizes.length > 0 ? (
    <SizeToggle
      title={title}
      disabled={disabledSizes.length > 0 || sizes.length === 1}
      data-testid="change-height"
      sizes={sizes}
      currentSize={currentSize}
      onChangeSize={size => {
        if (size !== currentSize) {
          onSizeChange(
            size,
            setFilterState,
            framesWithSameDepth,
            replaceableDoors,
            'depth',
            'height'
          );
        }
      }}
    />
  ) : (
    <></>
  );
};

export default Height;
